import get from "lodash/get"
import find from "lodash/find"
import "vanilla-cookieconsent"
import "publicCSS/banner.scss"
import pull from "lodash/pull"
import keys from "lodash/keys"
import Cookies from "js-cookie"
import keyBy from "lodash/keyBy"
import filter from "lodash/filter"
import isEmpty from "lodash/isEmpty"
import compact from "lodash/compact"
import "vanilla-cookieconsent/dist/cookieconsent.css"

// Check consent initialization requirements
const consentCheck = () => {
  const { country_code, site_state, ip_state } = window.amcb.settings
  const country = country_code
  const state = ip_state || site_state
  const inUS = country === "US"
  const mode = gpcDetected() || country !== "US" ? "opt-in" : "opt-out"

  const result = {
    display: true,
    mode: mode,
    enableTable: inUS,
    local: state,
  }
  Cookies.set("alpha_consent_check_v3", JSON.stringify(result))
  return result
}

// Check if a string is a valid URL
const validateHttpUrl = (string) => {
  let url
  try {
    url = new URL(string)
  } catch (_) {
    return false
  }
  return url.protocol === "http:" || url.protocol === "https:"
}

// Generate HTML for links in consent UI
const getLinksHTML = (settings = {}) => {
  let links = {}
  const linkText = {
    privacy: "Privacy Policy",
    notice: "Notice Of Right To Opt-out",
  }

  for (const setting in settings) {
    if (setting in linkText) {
      const dest = settings[setting]
      const external = validateHttpUrl(dest)
      const href = external ? dest : `/${dest}`
      const target = external ? "_blank" : "_self"
      const text = linkText[setting]
      links[
        setting
      ] = `<a href="${href}" class="cc-link" target="${target}">${text}</a>`
    }
  }

  return links
}

// Get the consent initialization configuration object
const getConsentConfig = () => {
  const { settings } = window.amcb
  const status = window.consentStatus

  let config = {
    revision: 1,
    autorun: status.display,
    current_lang: "en",
    cookie_name: "alpha_consent_v3",
    mode: status.mode,
    page_scripts: true,
    autoclear_cookies: true,
    remove_cookie_tables: true,
    gui_options: {
      consent_modal: {
        layout: settings.banner_style,
        position: settings.banner_position,
        transition: "slide",
        swap_buttons: false,
      },
      settings_modal: {
        layout: settings.modal_style,
        transition: "slide",
      },
    },
    onFirstAction: function (user_preferences, cookie) {
      updateGoogleConsent()
      stopCookiePulse()
    },
    onAccept: function (cookie) {
      updateGoogleConsent()
      stopCookiePulse()
    },
    onChange: function (cookie, changed_categories) {
      updateGoogleConsent()
      stopCookiePulse()
    },
    languages: {
      en: {
        consent_modal: {
          title: "We value your privacy",
          description:
            'Our site uses cookies to improve your experience. We may share information collected from cookies with our analytics and advertising partners. You may opt out of certain cookies if you wish, however, this may limit some features of the site. You may also <button id="s-o-bn" type="button" data-cc="c-settings" class="cc-link">Customize your choices</button>.',
          primary_btn: {
            text: "Accept all",
            role: "accept_all",
          },
          secondary_btn: {
            text: "Reject all",
            role: "accept_necessary",
          },
        },
        settings_modal: {
          title: "Cookie Preferences",
          save_settings_btn: "Save settings",
          accept_all_btn: "Accept all",
          reject_all_btn: "Reject all",
          close_btn_label: "Close",
          cookie_table_headers: [
            { name: "Name" },
            { platform: "Platform" },
            { expiration: "Expiration" },
            { description: "Description" },
          ],
          blocks: [
            {
              title: "Cookie usage",
              description:
                "Our site uses cookies to ensure the basic functionalities of the website and to enhance your online experience. Information collected from some cookies may be shared with our analytics and marketing partners. You may opt-in or opt-out of each category of cookie. Opting out of some categories may impact your experience on the website. For more details relative to cookies and other sensitive data, please read the full [privacy].",
            },
            gpcNotice(),
            {
              title: "Necessary",
              description:
                "Necessary cookies are required to enable the basic features of this site, such as providing secure log-in or adjusting your consent preferences. These cookies do not store any personally identifiable data.",
              toggle: {
                value: "necessary",
                enabled: true,
                readonly: true,
              },
            },
            {
              title: "Functional",
              description:
                "Functional cookies help perform certain functionalities like remembering user preferences, collecting feedback, and other third-party features.",
              toggle: {
                value: "functional",
                enabled: status.enableTable,
                readonly: false,
              },
            },
            {
              title: "Analytics",
              description:
                "Analytical cookies are used to understand how visitors interact with the website. These cookies help provide information on metrics such as the number of visitors, bounce rate, traffic source, etc.",
              toggle: {
                value: "analytics",
                enabled: status.enableTable,
                readonly: false,
              },
            },
            {
              title: "Advertisement/Targeting",
              description:
                "Advertisement and Targeting cookies are used to provide visitors with customized advertisements based on the pages you visited previously and to analyze the effectiveness of the ad campaigns.",
              toggle: {
                value: "targeting",
                enabled: status.enableTable,
                readonly: false,
              },
            },
            {
              title: "Other",
              description:
                "Other cookies are those that are being analyzed and have not been classified into a category as yet.",
              toggle: {
                value: "uncategorized",
                enabled: status.enableTable,
                readonly: false,
              },
            },
            {
              title: "More information",
              description: "To review your rights, visit [notice].",
            },
          ],
        },
      },
    },
  }

  if (gpcDetected()) {
    config.languages.en.consent_modal.description =
      'A Global Privacy Control (GPC) signal was received from your browser. We have honored your request by opting you out of all non-essential cookies and personal data processing. You may also <button id="s-o-bn" type="button" data-cc="c-settings" class="cc-link">Customize your choices</button>.'
    config.languages.en.consent_modal.primary_btn = {
      text: "Close",
      role: "accept_selected",
    }
    delete config.languages.en.consent_modal.secondary_btn
  }

  const links = getLinksHTML(settings)
  for (const language in config.languages) {
    const blocks = updateCookieTables(
      config.languages[language].settings_modal.blocks,
      links
    )
    config.languages[language].settings_modal.blocks = blocks
  }

  if (settings.hasOwnProperty("modal_position")) {
    config.gui_options.settings_modal.position = settings.modal_position
  }
  return config
}

// Set/Update cookie tables for each category of cookies
const updateCookieTables = (blocks, links = false) => {
  const { cookies } = window.amcb
  const cookieTables = getCookieTables(cookies)
  const updatedBlocks = compact(blocks).map((block) => {
    if (links) {
      for (const linkType in links) {
        const token = `[${linkType}]`
        block.description = block.description.replace(token, links[linkType])
      }
    }

    const table = get(cookieTables, block.toggle?.value, null)
    if (!isEmpty(table)) block.cookie_table = table
    return block
  })

  return updatedBlocks
}

// Set consent UI theme color scheme
const setThemeColor = (theme = "light") => {
  document.body.classList.add(`c_${theme}mode`)
}

// Find cookies with variable names using regular expression
const regexMatchCookie = (name, regexCookies = []) => {
  let cookieMeta
  regexCookies.forEach((meta) => {
    const regex = new RegExp(meta.name)
    if (name.match(regex)) cookieMeta = meta
  })
  return cookieMeta
}

// Get cookie tables for all categories
const getCookieTables = (knownCookies) => {
  const kc = keyBy(knownCookies, "name")
  const regexCookies = filter(knownCookies, "is_regex")

  let cookieTables = {
    necessary: [],
    functional: [],
    analytics: [],
    targeting: [],
    uncategorized: [],
  }
  const allCookies = Cookies.get()
  for (const name in allCookies) {
    if (!isEmpty(allCookies[name])) {
      const meta = name in kc ? kc[name] : regexMatchCookie(name, regexCookies)
      let category = get(meta, "category", "uncategorized")
      category = keys(cookieTables).includes(category)
        ? category
        : "uncategorized"
      const metaName = get(meta, "name", name)
      if (!find(cookieTables[category], ["name", metaName])) {
        cookieTables[category].push({
          name: metaName,
          platform: get(meta, "platform", "Unknown"),
          expiration: get(meta, "expiration", "Unknown"),
          description: get(meta, "description", "-"),
          is_regex: get(meta, "is_regex", false),
        })
      }
    }
  }
  return cookieTables
}

// Pause cookie table updates while settings modal is open
const setPulseListeners = () => {
  const BannerBtnIds = ["c-p-bn", "c-s-bn"]
  BannerBtnIds.forEach((btnID) => {
    const buttonElement = document.getElementById(btnID)
    if (!buttonElement) return
    buttonElement.dataset.gaCategory = "Consent Banner"
    buttonElement.dataset.gaLabel =
      btnID === "c-p-bn" ? "Accept All" : "Reject All"
    buttonElement?.addEventListener("click", buttonAnalyticsHandler)
  })
  const ActionBtnIds = ["s-all-bn", "s-rall-bn", "s-sv-bn"]
  ActionBtnIds.forEach((btnID) => {
    const buttonElement = document.getElementById(btnID)
    buttonElement?.addEventListener("click", settingsActionHandler)
    buttonElement?.addEventListener("click", buttonAnalyticsHandler)
  })
  document
    .getElementById("s-c-bn")
    ?.addEventListener("click", settingsCloseHandler)

  setStopListener()
}

const setStopListener = () => {
  document
    .getElementById("s-o-bn")
    ?.addEventListener("click", settingsOpenHandler)
}

const buttonAnalyticsHandler = (event) => {
  const bannerButton = event.currentTarget
  let label = ""
  switch (bannerButton.id) {
    case "c-p-bn":
    case "s-all-bn":
      label = "Accept All"
      break
    case "c-s-bn":
    case "s-rall-bn":
      label = "Reject All"
      break
    case "s-sv-bn":
      label = "Customize Choices"
      break
  }
  window.gtag("event", "cookie_consent", {
    action: label,
  })
}

const settingsActionHandler = () => toggleScroll(false)
const settingsCloseHandler = () => {
  toggleScroll(false)
  startCookiePulse()
}

const settingsOpenHandler = () => {
  toggleScroll()
  stopCookiePulse()
}

// Update cookie tables and process scripts at a set interval
const startCookiePulse = (init = false) => {
  const triggerTime = Math.floor(Date.now() / 1000)

  if (init) {
    setPulseListeners()
    window.cookiePulseStart = triggerTime
  }
  const startTime = window.cookiePulseStart
  if (triggerTime - startTime > 60) return

  const pulse = get(window, "cookiePulse", false)
  if (pulse) stopCookiePulse()
  window.cookiePulse = setInterval(() => {
    window.consent.updateScripts()
    const languages = window.consent.getConfig("languages")
    for (const lang in languages) {
      const blocks = updateCookieTables(languages[lang].settings_modal.blocks)
      window.consent.getConfig("languages")[lang].settings_modal.blocks = blocks
      window.consent.updateLanguage(lang, true)
    }
    setStopListener()
    if (Math.floor(Date.now() / 1000) - window.cookiePulseStart)
      return stopCookiePulse()
  }, 10000)
}

// Stop cookie table updates and script processing
const stopCookiePulse = () => {
  const pulse = get(window, "cookiePulse", false)
  if (pulse) clearInterval(pulse)
  window.cookiePulse = false
}

// Toggle scrolling overlay background.
const toggleScroll = (lock = true) => {
  const action = lock ? "add" : "remove"
  document.body.classList[action](`noScroll`)
}

// Get current GTM consent state
const getGoogleConsentState = () => {
  const consent = get(window, "consent", false)

  if (isEmpty(consent)) return
  let allowAds, allowAnalytics, allowFunctional

  const userPref = consent.getUserPreferences()
  switch (userPref.accept_type) {
    case "all":
      allowAds = true
      allowAnalytics = true
      allowFunctional = true
      break

    case "necessary":
      allowAds = false
      allowAnalytics = false
      allowFunctional = false
      break

    case "custom":
      allowAds = userPref.rejected_categories.includes("targeting")
      allowAnalytics = userPref.rejected_categories.includes("analytics")
      allowFunctional = userPref.rejected_categories.includes("functional")
      break
  }

  return {
    ad_storage: allowAds ? "granted" : "denied",
    analytics_storage: allowAnalytics ? "granted" : "denied",
    functionality_storage: allowFunctional ? "granted" : "denied",
    personalization_storage: "granted",
    security_storage: "granted",
  }
}

// Array for GTM callbacks
const gtmConsentListeners = []

// Update local consent cookie and GMT via callbacks
const updateGoogleConsent = () => {
  const consent = getGoogleConsentState()
  window.consent.set("data", {
    value: {
      gtmConsent: consent,
      redactAds: consent.ad_storage === "denied",
    },
    mode: "update",
  })

  gtmConsentListeners.forEach((callback) => {
    callback(consent)
  })
}

// Set global listener for GTM to add callbacks
window.gtmConsentListener = (callback) => {
  gtmConsentListeners.push(callback)
}

const stateConsentDisplay = (status) => {
  const { site_state, ip_state } = window.amcb.settings
  const state = ip_state || site_state

  const universalLinks = document.querySelectorAll(`.amcb-universal`)
  const stateSpecificEls = document.querySelectorAll(`.amcb-state-specific`)
  universalLinks.forEach((el) =>
    status.display ? el.classList.remove("amcb-hidden") : el.remove()
  )

  stateSpecificEls.forEach((el) => {
    state && el.dataset.states.includes(state)
      ? el.classList.remove("amcb-hidden")
      : el.remove()
  })
}

const loadPrivacyConfig = (adConsent = null) => {
  // Send non-personalized ads signal when required.
  let npaGDPR = false
  let npaStates = false
  if (localVar.privacyBannerActive) {
    if (consentStatus && consentStatus.display) {
      if (consentStatus.mode === "opt-in") {
        // Enable on-personalized ad mode for EEA and GDPR.
        npaGDPR = true
      } else if (adConsent !== null && !adConsent) {
        // Enable restricted data processing (non-personalized ad mode) for States requiring consent opt-out.
        npaStates = true
      }
    }
  }
  window.amcbgoogletag = window.opscobid || window.googletag || {}
  window.amcbgoogletag.cmd.push(() => {
    // Enable on-personalized ad modes.
    if (npaGDPR) {
      googletag.pubads().setRequestNonPersonalizedAds(1)
    }
    if (npaStates) {
      googletag.pubads().setPrivacySettings({
        restrictDataProcessing: true,
      })
    }
  })
}

// Detect GPC signal
const gpcDetected = () => navigator.globalPrivacyControl

// Display GPC notice in settings when GPC signal is detected
const gpcNotice = () => {
  const noticeConfigBlock = {
    title: "Global Privacy Control Detected!",
    description:
      "A Global Privacy Control (GPC) signal was received from your browser. We have honored your request by opting you out of all non-essential cookies and personal data processing.",
  }
  return gpcDetected() ? noticeConfigBlock : undefined
}

// Automaticly opt-out when GPC signal is detected
const gpcOptOut = () => {
  const consent = window.consent
  if (!consent) return

  // Accept only manually opt-in categories
  const levels = consent.get("level")
  if (gpcDetected()) {
    window.gtag("event", "gcp_detected")
    consent.accept(pull(levels, "necessary"))
  }


}

// Switch to opt-in mode when GPC signal is detected
const gpcSetMode = (status) => {
  if (!gpcDetected()) return status
  status.mode = "opt-in"
  return status
}

document.body.addEventListener("amcbInitialized", (event) => {
  const adConsent = event.detail.ad_storage === "granted"
  loadPrivacyConfig(adConsent)
})

// Initialize cookie consent UI
document.addEventListener("DOMContentLoaded", async () => {
  window.amcb = amcb
  let sessionCheck = Cookies.get("alpha_consent_check_v3")
  sessionCheck = sessionCheck ? JSON.parse(sessionCheck) : false
  const status = gpcSetMode(sessionCheck ? sessionCheck : consentCheck())
  stateConsentDisplay(status)
  window.consentStatus = status
  const consent = initCookieConsent()
  window.consent = consent
  const consentConfig = getConsentConfig()
  consent.run(consentConfig)
  gpcOptOut()
  setThemeColor(amcb.settings.color_scheme)
  startCookiePulse(true)
  // Dispatch initialization event
  const amcbInitialized = new CustomEvent("amcbInitialized", {
    detail: getGoogleConsentState(),
  })
  document.body.dispatchEvent(amcbInitialized)
})
